import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, IconButton } from '@dopomogai/ui';
import { ReactSortable } from 'react-sortablejs';
import { Plus, Trash, GripVertical } from 'lucide-react';
import { SlideData } from '@dopomogai/types';
import clsx from 'clsx';

interface SlideManagerProps {
  slides: SlideData[];
  activeSlideId: string | null;
  onAction: (action: 'select' | 'delete' | 'add' | 'reorder', payload?: any) => void;
}

export const SlideManager: React.FC<SlideManagerProps> = ({
  slides,
  activeSlideId,
  onAction,
}) => {
  const { t } = useTranslation(['slide_manager', 'common']);
  
  // DRAG-AND-DROP FIX: ReactSortable needs a plain array of items.
  // We use the original slide objects directly.
  const handleSortEnd = (newSlides: SlideData[]) => {
    // The library gives us the reordered array. We just pass it up.
    // App.tsx is responsible for re-calculating the `order` property.
    onAction('reorder', { slides: newSlides });
  };

  return (
    <div className="h-full flex flex-col bg-slate-50 border-r-2 border-black">
      <div className="flex-none p-4 border-b-2 border-black">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-slate-800">{t('slides_title', 'Slides')}</h2>
          <Button
            variant="secondary" size="sm" onClick={() => onAction('add')}
            icon={<Plus size={16} />} text={t('common:add', 'Add')}
          />
        </div>
      </div>
      <div className="flex-grow overflow-y-auto p-4">
        <ReactSortable
          list={slides} // Use the slides prop directly
          setList={handleSortEnd}
          animation={200}
          handle=".drag-handle" // The class to target for dragging
          ghostClass="opacity-40"
          className="space-y-3"
        >
          {slides.map((slide, index) => (
            <div
              key={slide.id}
              className={clsx(
                // This 'group' class is what makes group-hover work for the delete button
                'group relative bg-white border-2 rounded-lg cursor-pointer transition-all duration-200',
                slide.id === activeSlideId
                  ? 'border-emerald-500 shadow-[3px_3px_0_0_#059669]'
                  : 'border-black hover:-translate-x-1 hover:-translate-y-1 hover:shadow-[4px_4px_0_0_rgb(17,24,39)] active:translate-x-0 active:translate-y-0 active:shadow-none'
              )}
              onClick={() => onAction('select', { id: slide.id })}
              tabIndex={0} 
              onKeyDown={(e) => { if (e.key === 'Enter') onAction('select', { id: slide.id }); }}
            >
              <div className="flex items-center p-3">
                  <div className="drag-handle cursor-grab active:cursor-grabbing p-2 text-slate-400 hover:text-slate-700">
                    <GripVertical size={18} />
                  </div>
                  <div className="flex items-center gap-3 flex-grow min-w-0">
                    <div className="flex h-12 w-20 items-center justify-center rounded bg-slate-200 text-slate-500 font-mono text-xl font-bold">
                      {index + 1}
                    </div>
                    <div className="flex-grow min-w-0">
                      <p className="font-semibold text-slate-800 truncate">{slide.title}</p>
                      <p className="text-xs text-slate-500 capitalize">{slide.type} Slide</p>
                    </div>
                  </div>
                  {/* DELETE BUTTON FIX: Wrapped IconButton in a div whose opacity is controlled by the parent group-hover */}
                  <div className="pl-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 focus-within:opacity-100">
                    <IconButton
                      ariaLabel={t('delete_slide', 'Delete Slide')}
                      icon={<Trash size={16} />}
                      size="sm"
                      variant="ghost"
                      className="hover:bg-red-100 hover:text-red-600"
                      onClick={(e) => { e.stopPropagation(); onAction('delete', { id: slide.id }); }}
                    />
                  </div>
              </div>
            </div>
          ))}
        </ReactSortable>
        {slides.length === 0 && (
          <div className="text-center py-16 text-slate-500">
            <p>{t('no_slides_message', 'Add a slide to get started.')}</p>
          </div>
        )}
      </div>
    </div>
  );
};